import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { ShipmentChargeLine } from '../../models/charge-line/shipment-charge-line';
import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { Shipment } from '../../models/shipment';
import { TripCostSummaryComponent } from '../trip-cost-summary/trip-cost-summary.component';

export type ShipmentFinancialsCommand =
    | { type: 'add-charge' }
    | { type: 'edit-charge'; chargeLine: ShipmentChargeLine };

type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

/** Read-only financial workspace. The shipment route shell owns every mutation. */
@Component({
    selector: 'app-shipment-financials-panel',
    standalone: true,
    templateUrl: './shipment-financials-panel.component.html',
    styleUrls: ['./shipment-financials-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ButtonModule, TagModule, TooltipModule, TripCostSummaryComponent]
})
export class ShipmentFinancialsPanelComponent {
    readonly shipment = input.required<Shipment>();
    readonly deliveryById = input<Map<number, ShipmentDelivery>>(new Map());
    readonly tripCostRefreshKey = input(0);
    readonly canEditCharges = input(false);
    readonly command = output<ShipmentFinancialsCommand>();

    totalBuyingCharges(): number {
        return (this.shipment().chargeLines ?? []).reduce((sum, line) => sum + (line.buyingChargeAmount ?? 0), 0);
    }

    totalSellingCharges(): number {
        return (this.shipment().chargeLines ?? []).reduce((sum, line) => sum + (line.sellingChargeAmount ?? 0), 0);
    }

    /**
     * Shipment-level selling is represented by generated delivery transport
     * lines as well. Exclude them here because shipmentBaseSellingTotal already
     * supplies that same commercial amount to the summary.
     */
    supplementalSellingCharges(): number {
        return (this.shipment().chargeLines ?? [])
            .filter(line => !this.isDeliveryAssignmentTransportCharge(line))
            .reduce((sum, line) => sum + (line.sellingChargeAmount ?? 0), 0);
    }

    shipmentBaseBuyingTotal(): number {
        // Shipment buying/resource fields are legacy-only. Delivery snapshots
        // and their generated cost lines are the current cost authority.
        return 0;
    }

    shipmentBaseSellingTotal(): number {
        return (this.shipment().sellingPrice ?? 0) * (this.shipment().quantityRequired ?? 0);
    }

    shipmentRevenueTotal(): number {
        return this.shipmentBaseSellingTotal() + this.supplementalSellingCharges();
    }

    shipmentCostTotal(): number {
        return this.shipmentBaseBuyingTotal() + this.totalBuyingCharges();
    }

    shipmentTransportChargeTotal(): number {
        return (this.shipment().chargeLines ?? [])
            .filter(line => String(line.chargeType || '').toUpperCase() === 'TRANSPORT')
            .reduce((sum, line) => sum + (line.buyingChargeAmount ?? 0), 0);
    }

    shipmentOtherBuyingChargeTotal(): number {
        return this.totalBuyingCharges() - this.shipmentTransportChargeTotal();
    }

    shipmentAssignedInternalCostTotal(): number {
        return this.shipmentDeliveries()
            .filter(delivery => this.isInternalTransportSource(delivery.transportFinancialSource || ''))
            .reduce((sum, delivery) => sum + (delivery.transportCostAmount ?? delivery.internalTransportCostAmount ?? 0), 0);
    }

    shipmentAssignedExternalCostTotal(): number {
        return this.shipmentDeliveries()
            .filter(delivery => this.isExternalTransportSource(delivery.transportFinancialSource || ''))
            .reduce((sum, delivery) => sum + (delivery.transportCostAmount ?? delivery.transportBuyingCostAmount ?? 0), 0);
    }

    commercialMargin(): number {
        return this.shipmentRevenueTotal() - this.shipmentCostTotal();
    }

    shipmentMarginPercentageLabel(): string {
        const revenue = this.shipmentRevenueTotal();
        return revenue > 0
            ? `${((this.commercialMargin() / revenue) * 100).toFixed(1)}% shipment-level margin`
            : 'No customer revenue recorded';
    }

    shipmentTransportSourceSummary(): string {
        const sources = this.shipmentTransportSources();
        if (!sources.length) return 'Set when deliveries are assigned';
        const internal = sources.some(source => this.isInternalTransportSource(source));
        const external = sources.some(source => this.isExternalTransportSource(source));
        if (internal && external) return 'Mixed internal and payable delivery finance';
        return internal ? 'Internal delivery transport' : 'Payable delivery transport';
    }

    shipmentAccountingRouteLabel(): string {
        const hasRevenue = this.shipmentRevenueTotal() > 0;
        const sources = this.shipmentTransportSources();
        const internal = sources.some(source => this.isInternalTransportSource(source));
        const external = sources.some(source => this.isExternalTransportSource(source));
        if (internal && external) return hasRevenue ? 'Receivable + mixed costs' : 'Mixed cost channels';
        if (internal) return hasRevenue ? 'Receivable + bulk cost' : 'Bulk cost when posted';
        if (external) return hasRevenue ? 'Receivable + payable' : 'Payable when posted';
        return hasRevenue ? 'Receivable when posted' : 'No finance staging';
    }

    shipmentAccountingRouteTitle(): string {
        const label = this.shipmentAccountingRouteLabel();
        return label === 'No finance staging' ? 'Nothing to post' : label;
    }

    shipmentAccountingRouteDetail(): string {
        const label = this.shipmentAccountingRouteLabel();
        if (label === 'No finance staging') return 'Add a customer price or transport cost before posting to Accounts.';
        if (label.includes('mixed')) return 'Posting creates receivable staging and reconciles each delivery cost by its assigned source.';
        if (label.includes('bulk cost')) return 'Internal transport creates bulk-cost staging in each truck-owning company.';
        if (label.includes('payable')) return 'Supplier transport creates payable staging in each truck-owning company.';
        return 'Customer selling creates receivable staging when posted to Accounts.';
    }

    shipmentAccountingRouteSeverity(): TagSeverity {
        const label = this.shipmentAccountingRouteLabel();
        if (label === 'No finance staging') return 'secondary';
        if (label.includes('mixed')) return 'warn';
        if (label.includes('bulk cost')) return 'success';
        return 'info';
    }

    chargeCurrency(): string {
        return this.shipment().countryOfOrder?.currencyCode ?? 'SAR';
    }

    formatShipmentAmount(value: number | undefined): string {
        return this.formatAmount(this.chargeCurrency(), value ?? 0);
    }

    internalCostWaitingLabel(): string {
        const shipment = this.shipment();
        const currency = shipment.pricingInternalCostCurrencyCode || this.chargeCurrency();
        const charge = this.formatAmount(currency, shipment.pricingInternalCostWaitingChargeAmount ?? 0);
        return `${shipment.pricingInternalCostWaitingThresholdMinutes ?? 0} min / ${charge}`;
    }

    deliveryFinanceRows(): Array<{ deliveryNumber: string; channel: string; payee: string; amount: number; currency: string }> {
        return this.shipmentDeliveries()
            .filter(delivery => !!delivery.transportFinancialSource || delivery.transportCostAmount != null)
            .map(delivery => ({
                deliveryNumber: delivery.deliveryNumber || `Delivery #${delivery.deliveryId}`,
                channel: this.deliveryFinanceChannelLabel(delivery),
                payee: delivery.transportPayeeName || (this.isInternalTransportSource(delivery.transportFinancialSource || '')
                    ? 'Internal bulk cost' : 'Finance incomplete'),
                amount: delivery.transportCostAmount
                    ?? delivery.internalTransportCostAmount
                    ?? delivery.transportBuyingCostAmount
                    ?? 0,
                currency: delivery.transportCostCurrencyCode || this.chargeCurrency()
            }));
    }

    hasLegacyPricingContext(): boolean {
        const shipment = this.shipment();
        return !!(shipment.buyingPriceSource || shipment.resourceOwnershipType || shipment.supplierCompanyId
            || shipment.supplierCrmClientId || shipment.pricingInternalCostAmount != null);
    }

    private deliveryFinanceChannelLabel(delivery: ShipmentDelivery): string {
        const truckCompany = delivery.deliveryVehicle?.resourceCompanyName;
        const ownerSuffix = truckCompany ? ` in ${truckCompany}` : '';
        const ownership = String(delivery.transportDriverOwnershipType || delivery.deliveryDriver?.resourceOwnershipType || '')
            .toUpperCase();
        if (ownership === 'FREELANCE' || ownership === 'FREELANCER' || ownership === 'SUPPLIER') return `Freelance driver payable${ownerSuffix}`;
        if (ownership === 'TRANSPORTER') return `Transporter payable${ownerSuffix}`;
        if (ownership === 'RENTAL') return `Rental internal bulk cost${ownerSuffix}`;
        if (ownership === 'SISTER_OWNED') return `Internal bulk cost${ownerSuffix}`;
        return this.isInternalTransportSource(delivery.transportFinancialSource || '')
            ? `Internal bulk cost${ownerSuffix}` : 'Finance incomplete';
    }

    buyingPriceSourceLabel(): string {
        const shipment = this.shipment();
        return this.pricingSourceLabel(shipment.buyingPriceSource || shipment.pricingRateSource);
    }

    buyingPriceSourceSeverity(): TagSeverity {
        const shipment = this.shipment();
        return this.pricingSourceSeverity(shipment.buyingPriceSource || shipment.pricingRateSource);
    }

    sellingPriceSourceLabel(): string {
        return this.pricingSourceLabel(this.shipment().sellingPriceSource);
    }

    sellingPriceSourceSeverity(): TagSeverity {
        return this.pricingSourceSeverity(this.shipment().sellingPriceSource);
    }

    recommendedBuyingLabel(): string {
        const value = this.shipment().recommendedBuyingPrice;
        return value == null ? 'No recommendation' : this.formatShipmentAmount(value);
    }

    routePricingSourceLabel(): string {
        return this.pricingSourceLabel(this.shipment().pricingRateSource);
    }

    currentBuyingPriceLabel(): string {
        return this.formatShipmentAmount(this.shipment().buyingPrice ?? 0);
    }

    currentSellingPriceLabel(): string {
        return this.formatShipmentAmount(this.shipment().sellingPrice ?? 0);
    }

    resourceOwnershipLabel(): string {
        return this.pricingSourceLabel(this.shipment().resourceOwnershipType || 'OWNED');
    }

    supplierPricingReferenceLabel(): string {
        const shipment = this.shipment();
        const refs = [
            shipment.supplierCompanyId ? `Supplier company #${shipment.supplierCompanyId}` : undefined,
            shipment.supplierCrmClientId ? `CRM client #${shipment.supplierCrmClientId}` : undefined,
            shipment.supplierContractId ? `Contract #${shipment.supplierContractId}` : undefined,
            shipment.supplierRateCardId ? `Rate card #${shipment.supplierRateCardId}` : undefined,
        ].filter(Boolean);
        return refs.length ? refs.join(' | ') : 'No supplier pricing reference';
    }

    internalPricingReferenceLabel(): string {
        const shipment = this.shipment();
        const refs = [
            shipment.resourceCompanyId ? `Resource company #${shipment.resourceCompanyId}` : undefined,
            shipment.pricingInternalCostCardId ? `Internal card #${shipment.pricingInternalCostCardId}` : undefined,
            shipment.transportPlanId ? `Plan #${shipment.transportPlanId}` : undefined,
            shipment.transportRouteId ? `Route #${shipment.transportRouteId}` : undefined,
        ].filter(Boolean);
        return refs.length ? refs.join(' | ') : 'No route pricing reference';
    }

    customerPricingContractLabel(): string {
        return this.shipment().pricingContractId ? `Contract #${this.shipment().pricingContractId}` : 'Not linked';
    }

    customerPricingRateCardLabel(): string {
        return this.shipment().pricingRateCardId ? `Rate card #${this.shipment().pricingRateCardId}` : 'No customer rate card';
    }

    hasPricingWarning(): boolean { return !!this.shipment().pricingWarning; }
    pricingWarningText(): string { return this.shipment().pricingWarning || ''; }
    hasPricingOverrideReason(): boolean { return !!this.shipment().pricingOverrideReason; }
    pricingOverrideReasonText(): string { return this.shipment().pricingOverrideReason || ''; }

    formatChargeType(chargeLine: ShipmentChargeLine): string {
        return String(chargeLine.chargeType || 'Charge').replace(/_/g, ' ');
    }

    chargeDeliveryLabel(chargeLine: ShipmentChargeLine): string {
        return chargeLine.deliveryId ? this.deliveryById().get(chargeLine.deliveryId)?.deliveryNumber || 'Shipment level' : 'Shipment level';
    }

    formatChargeAmount(chargeLine: ShipmentChargeLine, value: number | undefined): string {
        return this.formatAmount(chargeLine.chargeCurrency || this.chargeCurrency(), value ?? 0);
    }

    chargeMargin(chargeLine: ShipmentChargeLine): number {
        return (chargeLine.sellingChargeAmount ?? 0) - (chargeLine.buyingChargeAmount ?? 0);
    }

    chargeMarginPercent(chargeLine: ShipmentChargeLine): string {
        const selling = chargeLine.sellingChargeAmount ?? 0;
        return selling > 0 ? `${((this.chargeMargin(chargeLine) / selling) * 100).toFixed(1)}% margin` : 'No selling value';
    }

    showDate(value: number | undefined): string {
        return value ? formatDate(new Date(value), 'dd-MM-yyyy HH:mm', 'en-US') : '';
    }

    private shipmentDeliveries(): ShipmentDelivery[] {
        return (this.shipment().deliveries ?? []).filter(delivery => String(delivery.deliveryType || 'DELIVERY').toUpperCase() === 'DELIVERY');
    }

    private isDeliveryAssignmentTransportCharge(chargeLine: ShipmentChargeLine): boolean {
        return chargeLine.deliveryId != null
            && String(chargeLine.chargeType || '').toUpperCase() === 'TRANSPORT'
            && String(chargeLine.remarks || '').trim().toLowerCase() === 'transport charge from delivery assignment';
    }

    private shipmentTransportSources(): string[] {
        return this.shipmentDeliveries().map(delivery => delivery.transportFinancialSource).filter((source): source is string => !!source);
    }

    private isInternalTransportSource(source: string): boolean {
        return ['INTERNAL_TRANSPORT_COST', 'SISTER_INTERNAL_TRANSPORT_COST', 'CUSTOM_INTERNAL_TRANSPORT_COST', 'OWNED', 'SISTER_OWNED']
            .includes(this.normalizedPricingSource(source));
    }

    private isExternalTransportSource(source: string): boolean {
        return !this.isInternalTransportSource(source);
    }

    private pricingSourceLabel(source: string | undefined | null): string {
        switch (this.normalizedPricingSource(source)) {
            case 'INTERNAL_TRANSPORT_COST': return 'Internal cost';
            case 'SISTER_INTERNAL_TRANSPORT_COST': return 'Sister internal cost';
            case 'CUSTOM_INTERNAL_TRANSPORT_COST': return 'Custom internal cost';
            case 'SUPPLIER_DELIVERY_RATE_CARD': return 'Supplier rate card';
            case 'CUSTOMER_CONTRACT': return 'Customer contract';
            case 'RATE_CARD': return 'Rate card';
            case 'MANUAL': return 'Manual';
            case 'NO_MATCH': return 'No match';
            case 'OWNED': return 'Own fleet';
            case 'SISTER_OWNED': return 'Sister fleet';
            case 'FREELANCE': return 'Freelance supplier';
            case 'RENTAL': return 'Rental supplier';
            default: {
                const normalized = this.normalizedPricingSource(source);
                return normalized ? normalized.replace(/_/g, ' ') : 'Not recorded';
            }
        }
    }

    private pricingSourceSeverity(source: string | undefined | null): TagSeverity {
        switch (this.normalizedPricingSource(source)) {
            case 'INTERNAL_TRANSPORT_COST':
            case 'SISTER_INTERNAL_TRANSPORT_COST':
            case 'CUSTOMER_CONTRACT':
            case 'RATE_CARD':
            case 'OWNED':
            case 'SISTER_OWNED': return 'success';
            case 'CUSTOM_INTERNAL_TRANSPORT_COST':
            case 'MANUAL': return 'warn';
            case 'SUPPLIER_DELIVERY_RATE_CARD':
            case 'FREELANCE':
            case 'RENTAL': return 'info';
            case 'NO_MATCH': return 'danger';
            default: return 'secondary';
        }
    }

    private normalizedPricingSource(source: string | undefined | null): string {
        return (source || '').replace(/^TRANSPORT_PLAN_/i, '').trim().toUpperCase();
    }

    private formatAmount(currency: string, value: number): string {
        const prefix = currency ? `${currency} ` : '';
        return `${prefix}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
}
